import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import useMediaEdit, {
    CropGravity,
    EditBatch,
    EditSpec,
    FlowStep,
    GalleryFlow,
    MediaVersion,
    ProcessorInfo,
    gravityToObjectPosition,
} from './hooks/useMediaEdit';

/*
 * Bulk inline edit UI, shared by MediaV2 and the classic PhotoManager.
 * Two modes: uniform crop (aspect + gravity — preview is pure CSS
 * object-fit/object-position, exact for the server-side gravity crop with
 * zero network work) and processing flow (cloud-capable steps only;
 * before/after preview is server-rendered against the 800px thumbnail).
 */

export const CROP_ASPECTS: { label: string; value: number }[] = [
    { label: 'Square 1:1', value: 1 },
    { label: 'Portrait 4:5', value: 4 / 5 },
    { label: 'Story 9:16', value: 9 / 16 },
    { label: 'Landscape 16:9', value: 16 / 9 },
    { label: 'Classic 3:2', value: 3 / 2 },
    { label: 'Standard 4:3', value: 4 / 3 },
];

const GRAVITIES: CropGravity[][] = [
    ['northwest', 'north', 'northeast'],
    ['west', 'center', 'east'],
    ['southwest', 'south', 'southeast'],
];

/** Crop supports images + mp4; flows are images only. */
const CROP_MIMES = ['image/jpeg', 'image/png', 'video/mp4'];
const FLOW_MIMES = ['image/jpeg', 'image/png'];

export function describeSpec(spec: EditSpec | null | undefined): string {
    if (spec?.type === 'flow') {
        return (
            spec.label ??
            `Flow (${spec.flowDefinition?.length ?? '?'} steps)`
        );
    }
    if (spec?.type === 'crop') {
        const preset = CROP_ASPECTS.find(
            (a) => Math.abs(a.value - spec.aspectRatio) < 0.001,
        );
        const aspect = preset?.label ?? `${spec.aspectRatio.toFixed(2)}:1`;
        return `Crop ${aspect}, ${spec.gravity}`;
    }
    return 'Edit';
}

const ENVIRONMENT_NAMES: Record<number, string> = {
    1: 'Text',
    2: 'Cloud',
    3: 'Local',
};

const backdrop =
    'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6';
const sheet =
    'w-full rounded-[15px] border border-border bg-background p-5 shadow-xl';
const primaryBtn =
    'cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white disabled:opacity-50';
const quietBtn =
    'cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink disabled:opacity-40';

function statusLabel(status: EditBatch['status']): string {
    switch (status) {
        case 'pending':
            return 'Queued';
        case 'processing':
            return 'Processing';
        case 'completed':
            return 'Completed';
        case 'completed_with_errors':
            return 'Completed with errors';
        case 'failed':
            return 'Failed';
        case 'rolled_back':
            return 'Rolled back';
    }
}

/* ------------------------------------------------------------------ */
/* Edit configuration (crop | flow tabs) + side-by-side preview        */
/* ------------------------------------------------------------------ */

export function BulkEditModal({
    activationId,
    galleryId,
    media,
    onClose,
    onStarted,
}: {
    activationId: number;
    galleryId: number | null;
    media: any[];
    onClose: () => void;
    onStarted: (batch: EditBatch) => void;
}) {
    const {
        createEditBatch,
        listGalleryFlows,
        listProcessors,
        previewEdit,
    } = useMediaEdit();
    const [mode, setMode] = useState<'crop' | 'flow'>('crop');
    const [aspect, setAspect] = useState(1);
    const [gravity, setGravity] = useState<CropGravity>('center');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [flows, setFlows] = useState<GalleryFlow[] | null>(null);
    const [processors, setProcessors] = useState<ProcessorInfo[] | null>(
        null,
    );
    const [selectedFlowId, setSelectedFlowId] = useState<number | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewBusy, setPreviewBusy] = useState(false);

    const editable = useMemo(
        () =>
            media.filter((m) =>
                (mode === 'crop' ? CROP_MIMES : FLOW_MIMES).includes(
                    m?.mimeType ?? '',
                ),
            ),
        [media, mode],
    );
    const skipped = media.length - editable.length;
    const samples = editable.slice(0, 4);

    /* Parsed flows with cloud-compatibility, resolved against the registry. */
    const flowOptions = useMemo(() => {
        if (!flows || !processors) return null;
        const byName: Record<string, ProcessorInfo> = {};
        processors.forEach((p) => (byName[p.name] = p));

        return flows.map((flow) => {
            let steps: FlowStep[] = [];
            let parseError = false;
            try {
                steps = JSON.parse(flow.flowDefinition);
            } catch {
                parseError = true;
            }
            const localOnly = steps
                .map((s) => s.name)
                .filter((name) => !byName[name]?.environments?.includes('cloud'));
            return {
                flow,
                steps,
                label: `Flow #${flow.id} (${
                    ENVIRONMENT_NAMES[flow.environmentId] ?? '?'
                })`,
                summary: steps.map((s) => s.name).join(' → '),
                incompatible: parseError || localOnly.length > 0,
                reason: parseError
                    ? 'Definition is not valid JSON'
                    : localOnly.length > 0
                    ? `Runs on-device only: ${Array.from(
                          new Set(localOnly),
                      ).join(', ')}`
                    : null,
            };
        });
    }, [flows, processors]);

    useEffect(() => {
        if (mode !== 'flow' || flows != null) return;
        if (galleryId == null) {
            setFlows([]);
            setProcessors([]);
            return;
        }
        listGalleryFlows(galleryId)
            .then(setFlows)
            .catch(() => setFlows([]));
        listProcessors()
            .then(setProcessors)
            .catch(() => setProcessors([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, galleryId]);

    const selectedOption =
        flowOptions?.find((o) => o.flow.id === selectedFlowId) ?? null;

    const buildSpec = (): EditSpec | null => {
        if (mode === 'crop') {
            return { type: 'crop', aspectRatio: aspect, gravity };
        }
        if (!selectedOption || selectedOption.incompatible) return null;
        return {
            type: 'flow',
            flowDefinition: selectedOption.steps,
            label: selectedOption.label,
        };
    };

    const renderPreview = async () => {
        const spec = buildSpec();
        const sample = samples[0];
        if (!spec || !sample || previewBusy) return;
        setPreviewBusy(true);
        setPreviewUrl(null);
        setError(null);
        try {
            const { url } = await previewEdit(sample.id, spec);
            setPreviewUrl(url);
        } catch (e: any) {
            setError(e?.response?.data?.error ?? 'Preview failed');
        }
        setPreviewBusy(false);
    };

    const apply = async () => {
        const spec = buildSpec();
        if (busy || editable.length === 0 || !spec) return;
        setBusy(true);
        setError(null);
        try {
            const batch = await createEditBatch(
                activationId,
                editable.map((m) => m.id),
                spec,
            );
            onStarted(batch);
        } catch (e: any) {
            setError(
                e?.response?.data?.error ?? 'Could not start the edit batch',
            );
            setBusy(false);
        }
    };

    const canApply =
        editable.length > 0 &&
        (mode === 'crop' ||
            (selectedOption != null && !selectedOption.incompatible));

    return (
        <div className={backdrop} onClick={onClose}>
            <div
                className={sheet + ' max-w-[620px]'}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-1 flex items-center gap-2">
                    <span className="ps-label">
                        Edit {editable.length}{' '}
                        {editable.length === 1 ? 'item' : 'items'}
                    </span>
                    <div className="ml-auto flex gap-1">
                        {(['crop', 'flow'] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => {
                                    setMode(m);
                                    setPreviewUrl(null);
                                    setError(null);
                                }}
                                className={
                                    'cursor-pointer rounded-full px-3 py-[4px] font-[inherit] text-[11.5px] font-semibold ' +
                                    (mode === m
                                        ? 'border border-transparent bg-brand-soft text-brand-ink'
                                        : 'border border-border bg-transparent text-muted-foreground')
                                }
                            >
                                {m === 'crop' ? 'Crop' : 'Processing flow'}
                            </button>
                        ))}
                    </div>
                </div>
                <p className="mb-3 text-[11.5px] text-faint">
                    Originals are kept as versions — every edit can be undone.
                    Anyone viewing a shared link sees the updated photo.
                    {skipped > 0 &&
                        ` ${skipped} unsupported item${
                            skipped === 1 ? '' : 's'
                        } ${
                            mode === 'flow'
                                ? '(flows are images only)'
                                : '(GIFs etc.)'
                        } will be skipped.`}
                </p>

                {mode === 'flow' && (
                    <div className="mb-4">
                        {flowOptions == null && (
                            <p className="py-2 text-[12.5px] text-faint">
                                Loading flows…
                            </p>
                        )}
                        {flowOptions?.length === 0 && (
                            <p className="py-2 text-[12.5px] text-faint">
                                No processing flows saved for this gallery —
                                create one under Processing first.
                            </p>
                        )}
                        {(flowOptions ?? []).map((o) => (
                            <button
                                key={o.flow.id}
                                type="button"
                                disabled={o.incompatible}
                                title={o.reason ?? undefined}
                                onClick={() => {
                                    setSelectedFlowId(o.flow.id);
                                    setPreviewUrl(null);
                                }}
                                className={
                                    'mb-1 flex w-full cursor-pointer items-baseline gap-2.5 rounded-[10px] border px-3 py-2 text-left font-[inherit] disabled:cursor-not-allowed disabled:opacity-45 ' +
                                    (selectedFlowId === o.flow.id
                                        ? 'border-transparent bg-brand-soft'
                                        : 'border-border bg-transparent')
                                }
                            >
                                <span className="text-[12.5px] font-semibold">
                                    {o.label}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-[11px] text-faint">
                                    {o.summary || '(empty)'}
                                </span>
                                {o.incompatible && (
                                    <span className="text-[10.5px] font-semibold text-red-500">
                                        on-device only
                                    </span>
                                )}
                            </button>
                        ))}

                        {/* server-rendered before/after on the first sample */}
                        {selectedOption && !selectedOption.incompatible && (
                            <div className="mt-2 flex items-center gap-3">
                                {samples[0] && (
                                    <img
                                        src={
                                            samples[0].thumbnailUri ??
                                            samples[0].originalUri
                                        }
                                        alt=""
                                        className="h-[88px] rounded-[8px] object-contain"
                                    />
                                )}
                                <span className="text-[11px] text-faint">
                                    →
                                </span>
                                {previewUrl ? (
                                    <img
                                        src={previewUrl}
                                        alt=""
                                        className="h-[88px] rounded-[8px] object-contain"
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        disabled={previewBusy}
                                        onClick={renderPreview}
                                        className={quietBtn + ' text-[12px]'}
                                    >
                                        {previewBusy
                                            ? 'Rendering…'
                                            : 'Render preview'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {mode === 'crop' && (
                    <>
                        {/* aspect presets */}
                <div className="mb-3 flex flex-wrap gap-1.5">
                    {CROP_ASPECTS.map((a) => (
                        <button
                            key={a.label}
                            type="button"
                            onClick={() => setAspect(a.value)}
                            className={
                                'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                (Math.abs(aspect - a.value) < 0.001
                                    ? 'border border-transparent bg-brand-soft text-brand-ink'
                                    : 'border border-border bg-transparent text-muted-foreground')
                            }
                        >
                            {a.label}
                        </button>
                    ))}
                </div>

                <div className="mb-4 flex items-start gap-5">
                    {/* gravity picker */}
                    <div>
                        <div className="ps-label mb-1.5">Keep</div>
                        <div className="grid grid-cols-3 gap-1">
                            {GRAVITIES.flat().map((g) => (
                                <button
                                    key={g}
                                    type="button"
                                    title={g}
                                    onClick={() => setGravity(g)}
                                    className={
                                        'h-7 w-7 cursor-pointer rounded-[6px] border ' +
                                        (gravity === g
                                            ? 'border-transparent bg-brand'
                                            : 'border-border bg-secondary hover:bg-border')
                                    }
                                />
                            ))}
                        </div>
                    </div>

                    {/* before/after samples */}
                    <div className="min-w-0 flex-1">
                        <div className="ps-label mb-1.5">Preview</div>
                        <div className="flex flex-wrap gap-3">
                            {samples.map((m) => {
                                const src =
                                    m.thumbnailUri ??
                                    m.thumbnail_800pxUri ??
                                    m.originalUri;
                                return (
                                    <div
                                        key={m.id}
                                        className="flex items-center gap-1.5"
                                    >
                                        <img
                                            src={src}
                                            alt=""
                                            className="h-[72px] max-w-[110px] rounded-[8px] object-contain"
                                        />
                                        <span className="text-[11px] text-faint">
                                            →
                                        </span>
                                        <div
                                            className="h-[72px] overflow-hidden rounded-[8px]"
                                            style={{
                                                width: `${Math.round(
                                                    72 * aspect,
                                                )}px`,
                                            }}
                                        >
                                            <img
                                                src={src}
                                                alt=""
                                                className="h-full w-full object-cover"
                                                style={{
                                                    objectPosition:
                                                        gravityToObjectPosition(
                                                            gravity,
                                                        ),
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                    </>
                )}

                {error && (
                    <p className="mb-2 text-[12px] font-semibold text-red-500">
                        {error}
                    </p>
                )}

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        disabled={busy || !canApply}
                        onClick={apply}
                        className={primaryBtn}
                    >
                        {busy
                            ? 'Starting…'
                            : `${mode === 'crop' ? 'Crop' : 'Process'} ${
                                  editable.length
                              } item${editable.length === 1 ? '' : 's'}`}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className={quietBtn + ' text-faint'}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Batch progress + report + undo                                      */
/* ------------------------------------------------------------------ */

export function EditBatchProgress({
    batch: initial,
    onClose,
    onChanged,
}: {
    batch: EditBatch;
    onClose: () => void;
    /** Fired whenever server state changed in a way the grid should reload for. */
    onChanged: () => void;
}) {
    const { getEditBatch, rollbackEditBatch } = useMediaEdit();
    const [batch, setBatch] = useState<EditBatch>(initial);
    const [rollbackBusy, setRollbackBusy] = useState(false);

    const running =
        batch.status === 'pending' || batch.status === 'processing';

    useEffect(() => {
        if (!running) return;
        const timer = setInterval(async () => {
            const fresh = await getEditBatch(batch.id).catch(() => null);
            if (!fresh) return;
            setBatch(fresh);
            const stillRunning =
                fresh.status === 'pending' || fresh.status === 'processing';
            if (!stillRunning) onChanged();
        }, 1500);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batch.id, running]);

    const items = batch.items ?? [];
    const done = items.filter((i) => i.status !== 'pending').length;
    const failures = items.filter((i) => i.status === 'failed');

    const undo = async () => {
        if (rollbackBusy) return;
        setRollbackBusy(true);
        const updated = await rollbackEditBatch(batch.id).catch(() => null);
        setRollbackBusy(false);
        if (updated) {
            setBatch((b) => ({ ...b, ...updated }));
            onChanged();
        }
    };

    return (
        <div className={backdrop} onClick={running ? undefined : onClose}>
            <div
                className={sheet + ' max-w-[420px]'}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ps-label mb-1">
                    {describeSpec(batch.spec)} — {statusLabel(batch.status)}
                </div>

                {running ? (
                    <>
                        <div className="mb-2 h-[6px] overflow-hidden rounded-full bg-secondary">
                            <div
                                className="h-full rounded-full bg-brand transition-[width]"
                                style={{
                                    width: `${
                                        batch.totalCount
                                            ? Math.round(
                                                  (done / batch.totalCount) *
                                                      100,
                                              )
                                            : 0
                                    }%`,
                                }}
                            />
                        </div>
                        <p className="text-[12px] text-faint">
                            {done} of {batch.totalCount} processed… Photos stay
                            unchanged until each one finishes.
                        </p>
                    </>
                ) : (
                    <>
                        <p className="mb-1 text-[12.5px]">
                            {batch.succeededCount} edited
                            {batch.failedCount > 0 &&
                                `, ${batch.failedCount} failed`}
                            {batch.status === 'rolled_back' &&
                                ' — rolled back'}
                        </p>
                        {batch.error && (
                            <p className="mb-1 text-[11.5px] text-red-500">
                                {batch.error}
                            </p>
                        )}
                        {failures.length > 0 && (
                            <div className="mb-2 max-h-[120px] overflow-y-auto rounded-[8px] bg-secondary p-2">
                                {failures.map((f) => (
                                    <p
                                        key={f.id}
                                        className="text-[11px] text-faint"
                                    >
                                        #{f.mediaId}: {f.error}
                                    </p>
                                ))}
                            </div>
                        )}
                        <div className="mt-2 flex items-center gap-3">
                            {(batch.status === 'completed' ||
                                batch.status === 'completed_with_errors') && (
                                <button
                                    type="button"
                                    disabled={rollbackBusy}
                                    onClick={undo}
                                    className={quietBtn}
                                >
                                    {rollbackBusy
                                        ? 'Undoing…'
                                        : 'Undo this edit'}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className={quietBtn + ' text-faint'}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Batch history (per activation) with rollback                        */
/* ------------------------------------------------------------------ */

export function EditHistoryModal({
    activationId,
    onClose,
    onChanged,
}: {
    activationId: number;
    onClose: () => void;
    onChanged: () => void;
}) {
    const { listEditBatches, rollbackEditBatch } = useMediaEdit();
    const [batches, setBatches] = useState<EditBatch[] | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const refresh = () =>
        listEditBatches(activationId)
            .then(setBatches)
            .catch(() => setBatches([]));

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const rollback = async (batchId: number) => {
        if (busyId != null) return;
        setBusyId(batchId);
        await rollbackEditBatch(batchId).catch(() => null);
        setBusyId(null);
        refresh();
        onChanged();
    };

    return (
        <div className={backdrop} onClick={onClose}>
            <div
                className={
                    sheet + ' flex max-h-[60vh] max-w-[520px] flex-col'
                }
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ps-label mb-2.5">Edit history</div>
                <div className="flex-1 overflow-y-auto">
                    {batches == null && (
                        <p className="py-2 text-[12.5px] text-faint">
                            Loading…
                        </p>
                    )}
                    {batches?.length === 0 && (
                        <p className="py-2 text-[12.5px] text-faint">
                            No bulk edits yet.
                        </p>
                    )}
                    {(batches ?? []).map((b) => (
                        <div
                            key={b.id}
                            className="flex items-baseline gap-2.5 border-b border-border py-2 last:border-b-0"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-[12.5px] font-semibold">
                                    {describeSpec(b.spec)}
                                </p>
                                <p className="text-[11px] text-faint">
                                    {moment(b.createdAt).format(
                                        'MMM D, h:mm A',
                                    )}{' '}
                                    · {b.succeededCount}/{b.totalCount} edited
                                    {b.failedCount > 0 &&
                                        ` · ${b.failedCount} failed`}{' '}
                                    · {statusLabel(b.status)}
                                </p>
                            </div>
                            {(b.status === 'completed' ||
                                b.status === 'completed_with_errors') && (
                                <button
                                    type="button"
                                    disabled={busyId != null}
                                    onClick={() => rollback(b.id)}
                                    className={quietBtn + ' text-[12px]'}
                                >
                                    {busyId === b.id
                                        ? 'Rolling back…'
                                        : 'Roll back'}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className={quietBtn + ' mt-3 self-start text-faint'}
                >
                    Close
                </button>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Per-media version history with restore                              */
/* ------------------------------------------------------------------ */

export function VersionHistoryModal({
    media,
    onClose,
    onChanged,
}: {
    media: any;
    onClose: () => void;
    onChanged: () => void;
}) {
    const { listVersions, restoreVersion } = useMediaEdit();
    const [versions, setVersions] = useState<MediaVersion[] | null>(null);
    const [currentVersion, setCurrentVersion] = useState<number | null>(null);
    const [busyVersion, setBusyVersion] = useState<number | null>(null);

    const refresh = () =>
        listVersions(media.id)
            .then((r) => {
                setVersions(r.versions ?? []);
                setCurrentVersion(r.currentVersion ?? null);
            })
            .catch(() => setVersions([]));

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media.id]);

    const restore = async (versionNumber: number) => {
        if (busyVersion != null) return;
        setBusyVersion(versionNumber);
        await restoreVersion(media.id, versionNumber).catch(() => null);
        setBusyVersion(null);
        refresh();
        onChanged();
    };

    return (
        <div className={backdrop} onClick={onClose}>
            <div
                className={
                    sheet + ' flex max-h-[60vh] max-w-[480px] flex-col'
                }
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ps-label mb-2.5">Version history</div>
                <div className="flex-1 overflow-y-auto">
                    {versions == null && (
                        <p className="py-2 text-[12.5px] text-faint">
                            Loading…
                        </p>
                    )}
                    {versions?.length === 0 && (
                        <p className="py-2 text-[12.5px] text-faint">
                            No versions yet — versions are captured the first
                            time a photo is edited or replaced.
                        </p>
                    )}
                    {(versions ?? []).map((v) => {
                        const isCurrent =
                            v.versionNumber ===
                            (currentVersion ??
                                versions?.[0]?.versionNumber ??
                                null);
                        return (
                            <div
                                key={v.id}
                                className="flex items-center gap-3 border-b border-border py-2 last:border-b-0"
                            >
                                <img
                                    src={
                                        v.thumbnailTinyUri ??
                                        v.thumbnailUri ??
                                        v.originalUri
                                    }
                                    alt=""
                                    className="h-10 w-10 rounded-[6px] object-cover"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="text-[12.5px] font-semibold">
                                        v{v.versionNumber}
                                        {isCurrent && ' · current'}
                                        {v.source === 'backfill' &&
                                            ' · original'}
                                    </p>
                                    <p className="text-[11px] text-faint">
                                        {v.source} ·{' '}
                                        {moment(v.createdAt).format(
                                            'MMM D, h:mm A',
                                        )}
                                    </p>
                                </div>
                                {!isCurrent && (
                                    <button
                                        type="button"
                                        disabled={busyVersion != null}
                                        onClick={() =>
                                            restore(v.versionNumber)
                                        }
                                        className={quietBtn + ' text-[12px]'}
                                    >
                                        {busyVersion === v.versionNumber
                                            ? 'Restoring…'
                                            : 'Restore'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className={quietBtn + ' mt-3 self-start text-faint'}
                >
                    Close
                </button>
            </div>
        </div>
    );
}
